import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { registerTenantIdGetter } from '@/lib/api-interceptor'
import { listTenants } from '@/lib/tenants-api'

interface Tenant {
  id: string
  name: string
  slug?: string | null
  domain?: string | null
  settings?: Record<string, unknown>
  metadata?: {
    createdBy?: string
    status?: string
  }
  createdAt?: string | null
  updatedAt?: string | null
}

interface TenantContextType {
  tenantIds: string[]
  tenants: Record<string, boolean>
  allTenants: Tenant[]
  currentTenantId: string | null
  currentTenant: Tenant | null
  isLoading: boolean
  error: Error | null
  switchTenant: (tenantId: string) => void
  refreshTenants: () => Promise<void>
}

const TenantContext = createContext<TenantContextType>({
  tenantIds: [],
  tenants: {},
  allTenants: [],
  currentTenantId: null,
  currentTenant: null,
  isLoading: true,
  error: null,
  switchTenant: () => {},
  refreshTenants: async () => {},
})

const STORAGE_KEY = 'clear-care-current-tenant-id'

export function TenantProvider({ children }: { children: ReactNode }) {
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(() => {
    return typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
  })
  const [tenantIds, setTenantIds] = useState<string[]>([])
  const [tenants, setTenants] = useState<Record<string, boolean>>({})
  const [allTenants, setAllTenants] = useState<Tenant[]>([])
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const refreshTenants = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setTenantIds([])
        setTenants({})
        setCurrentTenantId(null)
        setCurrentTenant(null)
        setIsLoading(false)
        return
      }

      const allTenants = await listTenants()
      const ids = allTenants.map((t) => t.id)
      const map: Record<string, boolean> = {}
      ids.forEach((id) => { map[id] = true })

      setTenants(map)
      setTenantIds(ids)
      setAllTenants(allTenants)

      const stored = localStorage.getItem(STORAGE_KEY)
      let activeId: string | null = null

      if (stored && ids.includes(stored)) {
        activeId = stored
      } else if (ids.length > 0) {
        activeId = ids[0]
        localStorage.setItem(STORAGE_KEY, activeId)
      }

      setCurrentTenantId(activeId)

      if (activeId) {
        const selected = allTenants.find((t) => t.id === activeId)
        setCurrentTenant(selected ?? null)
      } else {
        setCurrentTenant(null)
      }

      setIsLoading(false)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Erro desconhecido'))
      setTenantIds([])
      setTenants({})
      setCurrentTenantId(null)
      setCurrentTenant(null)
      setIsLoading(false)
    }
  }, [])

  const switchTenant = useCallback((tenantId: string) => {
    localStorage.setItem(STORAGE_KEY, tenantId)
    window.location.reload()
  }, [])

  // Register tenant getter for API interceptor
  useEffect(() => {
    registerTenantIdGetter(() => currentTenantId)
  }, [currentTenantId])

  // Init: check session and load tenants
  useEffect(() => {
    let mounted = true

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return
      if (!session) {
        setIsLoading(false)
        localStorage.removeItem(STORAGE_KEY)
      } else {
        refreshTenants()
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return
        if (!session) {
          setTenantIds([])
          setTenants({})
          setCurrentTenantId(null)
          setCurrentTenant(null)
          setIsLoading(false)
          localStorage.removeItem(STORAGE_KEY)
        } else {
          refreshTenants()
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [refreshTenants])

  return (
    <TenantContext value={{
      tenantIds,
      tenants,
      allTenants,
      currentTenantId,
      currentTenant,
      isLoading,
      error,
      switchTenant,
      refreshTenants,
    }}>
      {children}
    </TenantContext>
  )
}

/** Hook for tenant context; separate export triggers react-refresh warning — hook must live next to provider in many codebases. */
// eslint-disable-next-line react-refresh/only-export-components -- hook paired with TenantProvider
export function useTenantContext() {
  return useContext(TenantContext)
}
